import React, { useEffect, useState } from "react";
import {
  FaClock,
  FaFire,
  FaTrophy,
  FaCalendarAlt,
  FaHistory,
  FaChartLine,
} from "react-icons/fa";
import { useAppTheme } from "../context/AppThemeContext";
import { useUsageTracker } from "../context/UsageTrackerContext";
import api from "../api/httpClient";
import { API_PATHS } from "../api/apiPaths";

export default function StudentTimeSpent() {
  const { isDarkMode } = useAppTheme();
  const { getCurrentSessionTime, getSessionDuration } = useUsageTracker();
  const [sessionTime, setSessionTime] = useState(0);
  const [usageStats, setUsageStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Update session time every second
  useEffect(() => {
    const interval = setInterval(() => {
      setSessionTime(getCurrentSessionTime());
    }, 1000);

    return () => clearInterval(interval);
  }, [getCurrentSessionTime]);

  // Fetch usage statistics from backend
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await api.get(API_PATHS.usage.studentStats);

        if (response.ok) {
          setUsageStats(response.data);
        }
      } catch (error) {
        console.error("Error fetching usage stats:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
    const interval = setInterval(fetchStats, 60000); // Refresh every minute

    return () => clearInterval(interval);
  }, []);

  const stats = usageStats || {
    totalTimeToday: 0,
    totalTimeThisWeek: 0,
    totalTimeThisMonth: 0,
    averageDailyTime: 0,
    consecutiveDays: 0,
    lastActive: new Date(),
    topFeatures: [],
  };

  return (
    <div className={`min-h-screen px-4 py-8 sm:px-6 ${isDarkMode ? "bg-slate-950" : "bg-slate-50"}`}>
      <div className="mx-auto max-w-5xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className={`text-3xl font-bold ${isDarkMode ? "text-white" : "text-slate-900"}`}>
            Your Platform Usage
          </h1>
          <p className={`mt-2 ${isDarkMode ? "text-slate-400" : "text-slate-600"}`}>
            Track your learning activity and engagement
          </p>
        </div>

        {/* Current Session */}
        <div className={`mb-6 overflow-hidden rounded-2xl border shadow-lg ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
          <div className="bg-gradient-to-r from-blue-600 to-teal-500 px-6 py-8">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-white/80">Current Session</p>
                <p className="mt-2 text-5xl font-bold text-white">{getSessionDuration(sessionTime)}</p>
              </div>
              <FaClock className="text-6xl text-white/20" />
            </div>
          </div>
          <div className="px-6 py-4">
            <p className={`text-xs ${isDarkMode ? "text-slate-500" : "text-slate-600"}`}>
              Active now • Last action: {getSessionDuration(Math.floor((Date.now() - stats.lastActive) / 1000))} ago
            </p>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="mb-8 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[
            {
              icon: FaCalendarAlt,
              label: "Today",
              value: getSessionDuration(stats.totalTimeToday),
              color: "from-blue-600 to-blue-400",
            },
            {
              icon: FaHistory,
              label: "This Week",
              value: getSessionDuration(stats.totalTimeThisWeek),
              color: "from-purple-600 to-purple-400",
            },
            {
              icon: FaChartLine,
              label: "This Month",
              value: getSessionDuration(stats.totalTimeThisMonth),
              color: "from-teal-600 to-teal-400",
            },
            {
              icon: FaFire,
              label: "Streak",
              value: `${stats.consecutiveDays} days`,
              color: "from-orange-600 to-orange-400",
            },
          ].map((stat, idx) => (
            <div
              key={idx}
              className={`overflow-hidden rounded-xl border ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}
            >
              <div className={`bg-gradient-to-r ${stat.color} h-2`} />
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-xs font-semibold uppercase tracking-wider ${isDarkMode ? "text-slate-500" : "text-slate-600"}`}>
                      {stat.label}
                    </p>
                    <p className={`mt-2 text-2xl font-bold ${isDarkMode ? "text-white" : "text-slate-900"}`}>
                      {stat.value}
                    </p>
                  </div>
                  <stat.icon className={`text-2xl text-white/20`} />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Daily Breakdown */}
        <div className={`mb-8 overflow-hidden rounded-2xl border ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
          <div className={`border-b px-6 py-4 ${isDarkMode ? "border-slate-800" : "border-slate-200"}`}>
            <h2 className={`text-lg font-bold ${isDarkMode ? "text-white" : "text-slate-900"}`}>
              Last 7 Days
            </h2>
          </div>
          <div className="p-6">
            {[
              { day: "Monday", time: 120, target: 180 },
              { day: "Tuesday", time: 150, target: 180 },
              { day: "Wednesday", time: 180, target: 180 },
              { day: "Thursday", time: 90, target: 180 },
              { day: "Friday", time: 200, target: 180 },
              { day: "Saturday", time: 60, target: 180 },
              { day: "Today", time: sessionTime, target: 180 },
            ].map((entry, idx) => (
              <div key={idx} className="mb-4 last:mb-0">
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-sm font-semibold ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>
                    {entry.day}
                  </span>
                  <span className={`text-xs font-semibold ${entry.time >= entry.target ? "text-green-500" : "text-orange-500"}`}>
                    {Math.floor(entry.time / 60)}m {entry.time % 60}s
                  </span>
                </div>
                <div className={`h-2 overflow-hidden rounded-full ${isDarkMode ? "bg-slate-800" : "bg-slate-200"}`}>
                  <div
                    className={`h-full rounded-full transition-all ${
                      entry.time >= entry.target
                        ? "bg-gradient-to-r from-green-500 to-teal-500"
                        : "bg-gradient-to-r from-blue-600 to-teal-500"
                    }`}
                    style={{ width: `${Math.min(100, (entry.time / entry.target) * 100)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top Features */}
        <div className={`overflow-hidden rounded-2xl border ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
          <div className={`border-b px-6 py-4 ${isDarkMode ? "border-slate-800" : "border-slate-200"}`}>
            <h2 className={`text-lg font-bold ${isDarkMode ? "text-white" : "text-slate-900"}`}>
              Most Used Features
            </h2>
          </div>
          <div className="p-6">
            {stats.topFeatures && stats.topFeatures.length > 0 ? (
              <div className="space-y-3">
                {stats.topFeatures.map((feature, idx) => (
                  <div key={idx} className="flex items-center justify-between">
                    <span className={`text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>
                      {feature.name}
                    </span>
                    <div className="flex items-center gap-2">
                      <div className={`h-2 w-24 rounded-full ${isDarkMode ? "bg-slate-800" : "bg-slate-200"}`}>
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-blue-600 to-teal-500"
                          style={{ width: `${(feature.visits / stats.topFeatures[0].visits) * 100}%` }}
                        />
                      </div>
                      <span className={`text-xs font-semibold ${isDarkMode ? "text-slate-500" : "text-slate-600"}`}>
                        {feature.visits}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className={`text-center text-sm ${isDarkMode ? "text-slate-500" : "text-slate-600"}`}>
                No usage data yet
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
